import type { ChessMenuContext, MenuScreen } from '../types.js';
import { createScreenShell, createMenuHeader, el, confirmDialog } from '@bn-games/ui-shell';
import { mountAccountHistorySection } from '@bn-games/history-core/ui';
import type { GameStats } from '@bn-games/game-history-client';
import { isLoggedIn } from '@bn-games/auth-client';
import { t } from '../../i18n/I18n.js';
import { historyStore } from '../../history/HistoryStore.js';
import { fullMoveCount, playerLabel, resultLabel } from '../../history/labels.js';
import { fetchChessStats, fetchOnlineHistory } from '../../history/serverSync.js';
import type { GameRecord } from '../../history/types.js';
import { terminationText } from '../../scenes/GameOverHost.js';
import { downloadRecordPgn } from '../exportRow.js';
import { backRow } from './backButton.js';

function dateLabel(ts: number): string {
  return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function subLine(record: GameRecord): string {
  const players = t('historyList.vsTemplate', { white: playerLabel(record, 'w'), black: playerLabel(record, 'b') });
  const result = `${resultLabel(record)} (${terminationText(record.termination)})`;
  return `${players} · ${result} · ${t('historyList.movesCount', { count: fullMoveCount(record) })}`;
}

export function createHistoryListScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function row(record: GameRecord, ctx: ChessMenuContext, onDelete: (() => void) | null): HTMLElement {
    const meta = el('div', { class: 'history-row__meta' }, [
      el('div', { class: 'history-row__title', text: `${dateLabel(record.finishedAt)} — ${t(`historyList.modes.${record.mode}`)}` }),
      el('div', { class: 'history-row__sub', text: subLine(record) }),
    ]);
    const trash = onDelete === null ? null : el('button', {
      class: 'history-row__icon-btn history-row__delete',
      attrs: { type: 'button', 'aria-label': t('historyList.deleteAria'), title: t('historyList.deleteAria') },
      text: '✕',
      on: {
        click: (e: Event) => {
          e.stopPropagation();
          void confirmDialog({ title: t('historyList.deleteConfirm'), message: '', danger: true }).then((ok) => {
            if (ok) onDelete();
          });
        },
      },
    });
    const download = el('button', {
      class: 'history-row__icon-btn history-row__download',
      attrs: { type: 'button', 'aria-label': t('exportGame.downloadPgn'), title: t('exportGame.downloadPgn') },
      text: '⤓',
      on: {
        click: (e: Event) => {
          e.stopPropagation();
          downloadRecordPgn(record);
        },
      },
    });
    const open = (): void => ctx.openReplay(record, 'history');
    return el('div', {
      class: 'history-row',
      attrs: { role: 'button', tabindex: '0' },
      on: {
        click: open,
        keydown: (e: Event) => {
          if (e.target !== e.currentTarget || !(e instanceof KeyboardEvent) || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          open();
        },
      },
    }, [meta, el('div', { class: 'history-row__actions' }, [download, trash])]);
  }

  function paint(root: HTMLElement, ctx: ChessMenuContext): void {
    frame?.remove();
    const shell = createScreenShell();
    frame = shell.frame;
    const mounted = shell.frame;
    const body = shell.body;
    body.appendChild(createMenuHeader({ title: t('historyList.title') }));

    // "Your account" — signed in only: the server's online games and a stats line.
    mountAccountHistorySection<GameRecord, GameStats>({
      body,
      strings: {
        title: t('historyList.accountSection.title'),
        loading: t('historyList.accountSection.loading'),
        empty: t('historyList.accountSection.empty'),
      },
      isLoggedIn,
      fetchRecords: fetchOnlineHistory,
      isStillMounted: () => frame === mounted,
      renderRow: (record) => row(record, ctx, null),
      fetchStats: fetchChessStats,
      renderStats: (stats) => t('historyList.accountSection.statsLine', {
        wins: stats.wins, losses: stats.losses, draws: stats.draws, played: stats.gamesPlayed,
      }),
    });

    // "On this device" — bot and hot-seat games, and online ones played signed out.
    if (isLoggedIn()) body.appendChild(el('div', { class: 'history-row__title', text: t('historyList.deviceSection.title') }));
    const records = historyStore.list();
    const list = el('div', { class: 'history-list' });
    if (records.length === 0) list.appendChild(el('div', { class: 'history-empty', text: t('historyList.empty') }));
    for (const record of records) {
      list.appendChild(row(record, ctx, () => {
        historyStore.delete(record.id);
        paint(root, ctx);
      }));
    }
    body.append(list, backRow(ctx));
    root.appendChild(frame);
  }

  return {
    mount: paint,
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
